import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  createMemoryRouter,
  RouterProvider,
  Outlet,
  useLocation,
  useNavigate,
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_RouteContext as RouteContext,
} from "react-router-dom";
import { sectionLayoutRoute } from "../../routes";
import { useRootCtx } from "../../lib/rootContext";
import { useSplitView } from "../../lib/splitView";
import { NavBlocker } from "../../lib/unsavedChanges";
import { useVisibleNav } from "../Header";
import type { NavItem } from "../../nav";

/**
 * The split view's RIGHT pane: a second, independent router showing any page
 * of the app beside the one in the main pane.
 *
 * It is a real router, not an iframe and not a bare component render, and that
 * is the whole design. Pages here own sub-tabs off a `/*` splat, use <NavLink>
 * pill strips, and redirect through loaders — all of which need a router
 * context to work at all. A MEMORY router gives them one that is genuinely
 * separate from the address bar, so clicking a sub-tab in this pane moves only
 * this pane, and the URL keeps pointing at the main one.
 *
 * What it deliberately does NOT duplicate: MSAL, the access hook, the bearer,
 * the request cache, the Header. Those live above both panes in RootLayout, so
 * a second pane costs one more route subtree — not a second signed-in app.
 */

type PaneRouter = ReturnType<typeof createMemoryRouter>;

// ── mounting a second router ──────────────────────────────────────────────────
// react-router REFUSES to mount a <Router> inside another one: RouterProvider
// asserts `!useInRouterContext()`, which is literally
// `useContext(LocationContext) != null`, and throws
// "You cannot render a <Router> inside another <Router>".
//
// So the pane detaches itself from the outer router by resetting the two
// contexts that make it visible, and only those:
//   * LocationContext -> null, which is exactly what that guard tests for;
//   * RouteContext -> empty, because `useRoutesImpl` reads the PARENT matches
//     from it to resolve relative paths, and inheriting the main pane's
//     matches would resolve this pane's routes against the wrong base.
// Everything the two panes are meant to share (auth, access, the split state,
// the request cache) is plain React context and is deliberately left alone.
//
// These are `UNSAFE_`-prefixed exports — the library's own marker for "public
// but internal-ish". The alternative designs were worse: an iframe means a
// second MSAL sign-in and a second copy of every fetch, and a second React
// root means re-providing every context as a fresh instance.
const EMPTY_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

const DetachedRouterRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <LocationContext.Provider value={null as unknown as React.ContextType<typeof LocationContext>}>
    <RouteContext.Provider value={EMPTY_ROUTE_CONTEXT}>{children}</RouteContext.Provider>
  </LocationContext.Provider>
);

// ── the pane's own root layout ────────────────────────────────────────────────
// Mirrors what RootLayout does for the main pane, minus everything that must
// exist only once (Header, auth gate, access banners).
const PaneRoot: React.FC = () => {
  // Read the shell's resolved checkpoint state through React context rather
  // than a prop: this element is built once, for a router created outside the
  // render, so a prop would be captured at mount and never update.
  const ctx = useRootCtx();
  return (
    <>
      <PanePathReporter />
      <Suspense fallback={<div className="p-8 text-sm text-content-subtle">Loading…</div>}>
        <Outlet context={ctx} />
      </Suspense>
      {/* The dirty-surface registry is global, so this guards navigations made
          INSIDE the pane with the same modal the main pane uses. */}
      <NavBlocker />
    </>
  );
};

/** Reports the pane's own navigation up, so the layout survives a reload and
 *  the swap button knows what is actually on screen. */
const PanePathReporter: React.FC = () => {
  const { pathname } = useLocation();
  const { setPanePath } = useSplitView();
  useEffect(() => { setPanePath(pathname); }, [pathname, setPanePath]);
  return null;
};

// ── page picker ───────────────────────────────────────────────────────────────

/** The nav item whose path best matches `pathname` (longest prefix wins, so
 *  `/trading-desk/cash/holdings` resolves to Cash Monitor). */
const itemForPath = (sections: ReturnType<typeof useVisibleNav>, pathname: string): NavItem | undefined => {
  let best: NavItem | undefined;
  for (const s of sections) {
    for (const g of s.groups) {
      for (const i of g.items) {
        if (pathname === i.to || pathname.startsWith(i.to + "/")) {
          if (!best || i.to.length > best.to.length) best = i;
        }
      }
    }
  }
  return best;
};

const Icon: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg viewBox="0 0 24 24" className={className ?? "h-4 w-4"} fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

const PaneChrome: React.FC<{ paneRouter: PaneRouter | null; canGoBack: boolean }> = ({ paneRouter, canGoBack }) => {
  const { path, closePane, setPanePath } = useSplitView();
  const { hasCheckpointAccess } = useRootCtx();
  const sections = useVisibleNav(hasCheckpointAccess);
  // These hooks resolve against the PRIMARY router: this component renders
  // inside RootLayout, above the pane's own RouterProvider.
  const primary = useLocation();
  const navigatePrimary = useNavigate();

  const current = itemForPath(sections, path);

  const swap = () => {
    if (!path) return;
    const toPrimary = path;
    setPanePath(primary.pathname); // the pane's effect navigates it
    navigatePrimary(toPrimary);    // may be intercepted by the unsaved-changes guard
  };

  return (
    <div className="flex items-center gap-1.5 border-b border-line bg-surface-sunken px-2 py-1.5">
      <button
        type="button"
        onClick={() => paneRouter?.navigate(-1)}
        disabled={!canGoBack}
        title="Back in this pane"
        aria-label="Back in this pane"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-content-subtle transition-colors hover:bg-surface hover:text-content disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Icon d="M15 18l-6-6 6-6" />
      </button>

      <label className="sr-only" htmlFor="split-pane-page">Page in the right pane</label>
      <select
        id="split-pane-page"
        value={current?.to ?? ""}
        onChange={(e) => setPanePath(e.target.value)}
        className="min-w-0 flex-1 truncate rounded border border-line bg-surface px-2 py-1 text-xs text-content focus:border-qw-teal focus:outline-none"
      >
        {!current && <option value="">Pick a page…</option>}
        {sections.map((s) => (
          <optgroup key={s.key} label={s.label}>
            {s.groups.flatMap((g) => g.items).map((i) => (
              <option key={i.key + i.to} value={i.to}>{i.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <button
        type="button"
        onClick={swap}
        disabled={!path}
        title="Swap the two panes"
        aria-label="Swap the two panes"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-content-subtle transition-colors hover:bg-surface hover:text-content disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Icon d="M7 16H3m0 0l3-3m-3 3l3 3M17 8h4m0 0l-3-3m3 3l-3 3" />
      </button>
      <button
        type="button"
        onClick={closePane}
        title="Close the right pane"
        aria-label="Close the right pane"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-content-subtle transition-colors hover:bg-surface hover:text-content"
      >
        <Icon d="M18 6L6 18M6 6l12 12" />
      </button>
    </div>
  );
};

// ── the pane ──────────────────────────────────────────────────────────────────

const SecondaryPane: React.FC = () => {
  const { path } = useSplitView();

  // The router is created ONCE, on the first render that has a real page to
  // show, and kept in a ref rather than useMemo.
  //
  // THE INITIAL ENTRY MUST BE A REAL PAGE — never "/" leaning on the landing
  // redirect. A <Navigate> that fires from a mount effect lands in exactly the
  // window where StrictMode's mount/unmount/remount has RouterProvider
  // unsubscribed from the router: the router moves, the view never hears
  // about it, and the pane renders blank forever while its own state says it
  // is elsewhere. Callers therefore pass a resolved path in (the Header
  // resolves the landing page); until one arrives the pane shows its picker
  // and mounts no router at all.
  const routerRef = useRef<PaneRouter | null>(null);
  if (!routerRef.current && path) {
    routerRef.current = createMemoryRouter(
      [{ path: "/", element: <PaneRoot />, children: [sectionLayoutRoute] }],
      { initialEntries: [path] },
    );
  }
  const paneRouter = routerRef.current;

  // How deep into its own history the pane is. react-router's RouterState
  // exposes no history index, so count the pane's pushes and pops instead —
  // the alternative, a Back button that is always enabled, silently does
  // nothing at the first entry.
  const [canGoBack, setCanGoBack] = useState(false);
  const depth = useRef(0);
  useEffect(() => {
    if (!paneRouter) return;
    return paneRouter.subscribe((st) => {
      if (st.historyAction === "PUSH") depth.current += 1;
      else if (st.historyAction === "POP") depth.current = Math.max(0, depth.current - 1);
      setCanGoBack(depth.current > 0);
    });
  }, [paneRouter]);

  // Someone outside the pane moved it (the picker, swap, "open in right pane").
  // Comparing against the router's OWN location is what keeps this from
  // looping with PanePathReporter: after the pane navigates itself and reports
  // up, the two agree and this does nothing.
  useEffect(() => {
    if (!paneRouter || !path) return;
    if (paneRouter.state.location.pathname !== path) void paneRouter.navigate(path);
  }, [path, paneRouter]);

  return (
    <section
      aria-label="Secondary pane"
      className="flex h-full min-w-0 flex-col overflow-hidden border-l border-line-strong"
    >
      <PaneChrome paneRouter={paneRouter} canGoBack={canGoBack} />
      <div className="min-h-0 flex-1 overflow-auto">
        {paneRouter ? (
          <DetachedRouterRoot>
            <RouterProvider router={paneRouter} />
          </DetachedRouterRoot>
        ) : (
          <p className="p-8 text-sm text-content-subtle">
            Pick a page above to show it beside the main one.
          </p>
        )}
      </div>
    </section>
  );
};

export default SecondaryPane;
